import { Product, ProductInfoComponent, ProductType } from '@inter-ikea-storageone/range';
import { ComponentIds, Entity, HierarchyUtils, Size } from '@inter-ikea-core/core';
import { CommunicationSizeComponent } from '@inter-ikea-core/core';
import { PlatsaSoftClosingHingeComponent } from '@inter-ikea-storageone/range-platsa';

const getSize = (entity: Entity) => {
  return entity.getComponent<CommunicationSizeComponent>(
    ComponentIds.COMMUNICATION_SIZE_COMPONENT_ID
  )?.size;
};

const getFrontsInScene = (entitiesInScene: Entity[]) => {
  return entitiesInScene.filter(entity => {
    const productType = entity.getComponent<ProductInfoComponent>(ProductInfoComponent.id)
      ?.productType;
    return (
      productType === ProductType.PLATSA_HINGE_DOOR_ASM ||
      productType === ProductType.PLATSA_SC_HINGE_DOOR_ASM ||
      productType === ProductType.PLATSA_DRAWER_ASM
    );
  });
};

export const matchInteriorsToFrames = (products: Product[], entitiesInScene: Entity[]) => {
  const framesInScene = entitiesInScene.filter(
    entity =>
      entity.getComponent<ProductInfoComponent>(ProductInfoComponent.id)?.productType ===
      ProductType.PLATSA_FRAME
  );

  if (framesInScene.length === 0) {
    return products;
  }
  const frameDepthsInScene = framesInScene.map(entity => getSize(entity)?.depth);
  const frameWidthsInScene = framesInScene.map(entity => getSize(entity)?.width);

  return products.filter(product => {
    if (product.type === ProductType.PLATSA_SHOE_SHELF) {
      return frameWidthsInScene.includes(product.communicatedDimensions.width);
    } else if (product.type === ProductType.PLATSA_RAIL_STRIP_W_HOOKS_ASM) {
      return frameDepthsInScene.includes(product.communicatedDimensions.depth);
    }
    return (
      frameDepthsInScene.includes(product.communicatedDimensions.depth) &&
      frameWidthsInScene.includes(product.communicatedDimensions.width)
    );
  });
};

export const matchFrontTypeToFramesInScene = (products: Product[], entitiesInScene: Entity[]) => {
  const frames = entitiesInScene.filter(
    entity =>
      entity.getComponent<ProductInfoComponent>(ProductInfoComponent.id)?.productType ===
      ProductType.PLATSA_FRAME
  );

  if (frames.length === 0) {
    return products;
  }

  const frontsInScene = getFrontsInScene(entitiesInScene);

  if (frontsInScene.length === 0) {
    //If we don't have any doors/fronts in the scene don't include drawers in the list
    return products.filter(p => p.type !== ProductType.PLATSA_DRAWER_ASM);
  }

  const frontTypesOnFrame = frontsInScene
    .map(front => front.getComponent<ProductInfoComponent>(ProductInfoComponent.id)?.productType)
    .filter((type): type is ProductType => type !== undefined);

  return products.filter(product => frontTypesOnFrame.includes(product.type));
};

export const matchDoorOpeningFunction = (products: Product[], entitiesInScene: Entity[]) => {
  if (entitiesInScene.length === 0) {
    return products.filter(product => product.type !== ProductType.PLATSA_SC_HINGE_DOOR_ASM);
  }
  const scene = HierarchyUtils.findWithComponentInUpwardsHierarchy(
    entitiesInScene[0],
    ComponentIds.SCENE_COMPONENT_ID
  );

  return scene?.hasComponent(PlatsaSoftClosingHingeComponent.id)
    ? products.filter(p => p.type !== ProductType.PLATSA_HINGE_DOOR_ASM)
    : products.filter(p => p.type !== ProductType.PLATSA_SC_HINGE_DOOR_ASM);
};

export const matchFrontSizeToFramesInScene = (products: Product[], entitiesInScene: Entity[]) => {
  const frames = entitiesInScene.filter(
    entity =>
      entity.getComponent<ProductInfoComponent>(ProductInfoComponent.id)?.productType ===
      ProductType.PLATSA_FRAME
  );

  const framesWithoutFronts = frames.filter(
    frame =>
      !HierarchyUtils.children(frame).some(child => {
        const childProductType = child.getComponent<ProductInfoComponent>(ProductInfoComponent.id)
          ?.productType;
        return (
          childProductType === ProductType.PLATSA_HINGE_DOOR_ASM ||
          childProductType === ProductType.PLATSA_SC_HINGE_DOOR_ASM ||
          childProductType === ProductType.PLATSA_DRAWER_ASM
        );
      })
  );

  if (frames.length === 0) {
    return products;
  }

  const frontsInScene = getFrontsInScene(entitiesInScene);
  const frontSizesOnFrames = frontsInScene
    .map(front => getSize(front))
    .filter((size): size is Size => size !== undefined);
  const defaultDoorSizesOnFramesWithoutFronts = framesWithoutFronts
    .map(frame => getSize(frame))
    .filter((size): size is Size => size !== undefined)
    .map(size =>
      size.width === 800
        ? { width: 400, height: size.height }
        : { width: size.width, height: size.height }
    );

  return products.filter(
    product =>
      frontSizesOnFrames.some(
        size =>
          size.width === product.communicatedDimensions.width &&
          size.height === product.communicatedDimensions.height
      ) ||
      defaultDoorSizesOnFramesWithoutFronts.some(
        size =>
          size.width === product.communicatedDimensions.width &&
          size.height === product.communicatedDimensions.height
      )
  );
};

export const matchHandleSizeToFrontsInScene = (products: Product[], entitiesInScene: Entity[]) => {
  const handleDoorConnectorEdgesDiff = 40;
  const doors = entitiesInScene.filter(e => e.hasComponent(ComponentIds.DOOR_COMPONENT_ID));
  const drawers = entitiesInScene.filter(e => e.hasComponent(ComponentIds.DRAWER_COMPONENT_ID));
  if (doors.length === 0 && drawers.length === 0) {
    return products;
  }
  const doorHeights = doors
    .map(door => {
      const doorHeight = door.getComponent<CommunicationSizeComponent>(
        ComponentIds.COMMUNICATION_SIZE_COMPONENT_ID
      )?.size.height;
      //120cm high PLATSA doors has a connector 30cm from edge so max handle size is 600
      return doorHeight === 1200 ? 600 : doorHeight;
    })
    .filter(Boolean) as number[];
  const drawerWidths = drawers
    .map(
      door =>
        door.getComponent<CommunicationSizeComponent>(ComponentIds.COMMUNICATION_SIZE_COMPONENT_ID)
          ?.size.width
    )
    .filter(Boolean) as number[];

  return products.filter(product => {
    const handleHeight = product.communicatedDimensions.height;
    return [...doorHeights, ...drawerWidths].some(
      size => handleHeight <= size - handleDoorConnectorEdgesDiff
    );
  });
};

export const matchClothRailOrganizersToFramesInScene = (
  products: Product[],
  entitiesInScene: Entity[]
) => {
  const clothRailsInScene = entitiesInScene.filter(e => {
    const productType = e.getComponent<ProductInfoComponent>(ProductInfoComponent.id)?.productType;
    return (
      productType &&
      [
        ProductType.PLATSA_CLOTH_RAIL_55_W_HOOKS_ASM,
        ProductType.PLATSA_CLOTH_RAIL_55,
        ProductType.PLATSA_OPEN_FRAME_HANGING
      ].includes(productType)
    );
  });
  return entitiesInScene.length === 0 || clothRailsInScene.length > 0 ? products : [];
};

export const matchShelfOrganizersToEntitiesInScene = (
  products: Product[],
  entitiesInScene: Entity[]
) => {
  if (entitiesInScene.length === 0) {
    return products;
  }
  const shelvesAndFrames = entitiesInScene.filter(e => {
    const productType = e.getComponent<ProductInfoComponent>(ProductInfoComponent.id)?.productType;
    return (
      productType &&
      [
        ProductType.PLATSA_FRAME,
        ProductType.PLATSA_OPEN_FRAME_SHELVES,
        ProductType.PLATSA_OPEN_FRAME_HANGING,
        ProductType.PLATSA_OPEN_FRAME_SHOES,
        ProductType.PLATSA_SHELF
      ].includes(productType)
    );
  });
  return products.filter(product =>
    shelvesAndFrames.some(entity => {
      const entitySize = entity.getComponent<CommunicationSizeComponent>(
        ComponentIds.COMMUNICATION_SIZE_COMPONENT_ID
      )?.size;
      return (
        entitySize &&
        ((entitySize.width > product.communicatedDimensions.width &&
          entitySize.depth > product.communicatedDimensions.depth) ||
          (entitySize.depth > product.communicatedDimensions.width &&
            entitySize.width > product.communicatedDimensions.depth))
      );
    })
  );
};

export const matchDrawerOrganizersToEntitiesInScene = (
  products: Product[],
  entitiesInScene: Entity[]
) => {
  if (entitiesInScene.length === 0) {
    return products;
  }
  const drawersInScene = entitiesInScene.filter(e => {
    const productType = e.getComponent<ProductInfoComponent>(ProductInfoComponent.id)?.productType;
    return productType && [ProductType.PLATSA_DRAWER_ASM].includes(productType);
  });
  return products.filter(product =>
    drawersInScene.some(entity => {
      const entitySize = entity.getComponent<CommunicationSizeComponent>(
        ComponentIds.COMMUNICATION_SIZE_COMPONENT_ID
      )?.size;
      return (
        entitySize &&
        ((entitySize.width > product.communicatedDimensions.width &&
          entitySize.depth > product.communicatedDimensions.depth) ||
          (entitySize.depth > product.communicatedDimensions.width &&
            entitySize.width > product.communicatedDimensions.depth))
      );
    })
  );
};
